/// <reference types="vite/client" />
import type { Cluster, Renderer } from "@googlemaps/markerclusterer";
import { BRAND_MAP_ID } from "@majourneys/shared";
import type { DiscoverSegment } from "../../types";

// Shared Google Maps configuration for the Discover experience: API key, the
// Map ID that carries the pale brand style, default camera, and the DOM cluster
// renderer. Used by the desktop DiscoverStage map and the mobile map backdrops
// so every surface renders the same branded map.

export const CITY_ZOOM = 13;
export const COUNTRY_ZOOM = 6;
// At/above this zoom the list narrows to "places in view". One past CITY_ZOOM,
// so picking a city (which flies to CITY_ZOOM) keeps the grouped browse and the
// flat in-view list only appears once the user actively zooms in further.
export const VIEWPORT_SYNC_ZOOM = CITY_ZOOM + 1;
export const MOROCCO_CENTER = { lat: 29.0, lng: -9.5 };
export const API_KEY =
  (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? "";
// AdvancedMarkerElement requires a Map ID, and the Map ID carries the pale
// brand cloud style every map uses (BRAND_MAP_ID in @majourneys/shared). The
// fallback is our own public Map ID, not Google's "DEMO_MAP_ID": the
// Cloudflare build sets the API key but never set this, so majourneys.com
// shipped Google's default basemap on every map (owner, 2026-09-29: "the map
// is not the same style as local"). A Map ID is not a
// secret, it is in every page that draws a map. The env var still wins.
export const MAP_ID =
  (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string | undefined) || BRAND_MAP_ID;

// Clamp to a sane Google Maps range so the zoom +/- buttons can't drive the
// camera past the tile limits (where the map stops responding but the step
// counter keeps running, desyncing the controls).
export function clampedZoom(base: number, step: number, min = 3, max = 20): number {
  return Math.min(max, Math.max(min, base + step));
}

export function pillIcon(segment: DiscoverSegment): string {
  if (segment === "restaurants") return "ri-restaurant-line";
  if (segment === "stays") return "ri-hotel-line";
  if (segment === "spas") return "ri-flower-line";
  if (segment === "guides") return "ri-user-voice-line";
  if (segment === "locations") return "ri-map-pin-line";
  if (segment === "golf") return "ri-golf-ball-line";
  if (segment === "nightlife") return "ri-goblet-line";
  if (segment === "beach-clubs") return "ri-umbrella-line";
  return "ri-ticket-2-line";
}

// ─────────────────────────────────────────────────────────────────
// Framing a city
// ─────────────────────────────────────────────────────────────────

/** A place on the map. Both apps carry the coordinate as nullable. */
export interface MapPoint {
  readonly latitude: number | null;
  readonly longitude: number | null;
}

/**
 * How far from the rest of its city a venue can sit and still get a vote on
 * where the camera goes. Roughly 25 km.
 *
 * A city's catalogue includes its day trips — the Agafay camps sold under
 * Marrakech, the waterfall excursions sold under Fez. Framing all of them
 * keeps the camera so wide that picking a city never visibly zooms, and pulls
 * the centre out into the desert instead of onto the medina. The day trips
 * stay on the map; they just stop dragging the view away from the city the
 * traveller asked for.
 */
export const CITY_CORE_SPAN_DEG = 0.25;

/** Mean of a set of coordinates. Null when nothing is mapped. */
export function meanLatLng(
  items: readonly MapPoint[],
): { lat: number; lng: number } | null {
  let lat = 0;
  let lng = 0;
  let n = 0;
  for (const i of items) {
    if (typeof i.latitude !== "number" || typeof i.longitude !== "number") continue;
    lat += i.latitude;
    lng += i.longitude;
    n += 1;
  }
  return n === 0 ? null : { lat: lat / n, lng: lng / n };
}

/**
 * The venues close enough to a city's centre of gravity to frame it. Hands
 * back the whole set when dropping the outliers would leave too little to
 * frame — a city with one mapped venue still has to be shown.
 */
export function cityCoreVenues<T extends MapPoint>(items: readonly T[]): readonly T[] {
  const centre = meanLatLng(items);
  if (!centre) return items;
  const core = items.filter(
    (i) =>
      typeof i.latitude === "number" &&
      typeof i.longitude === "number" &&
      Math.abs(i.latitude - centre.lat) < CITY_CORE_SPAN_DEG &&
      Math.abs(i.longitude - centre.lng) < CITY_CORE_SPAN_DEG,
  );
  return core.length >= 2 ? core : items;
}

// ─────────────────────────────────────────────────────────────────
// Opening on the traveller's own city
// ─────────────────────────────────────────────────────────────────

export interface CityCentroid {
  readonly lat: number;
  readonly lng: number;
  /** Mapped venues behind this centroid. */
  readonly count: number;
}

/**
 * Where each city actually is, averaged from its own venues.
 *
 * The stored city coordinates are not trustworthy — a past incident
 * overwrote most of them with a single point near Tétouan — but every venue
 * carries its own correct coordinate, so the venues are asked instead of the
 * city row.
 */
export function cityCentroids(
  items: readonly (MapPoint & { readonly citySlug?: string })[],
): Map<string, CityCentroid> {
  const acc = new Map<string, { lat: number; lng: number; count: number }>();
  for (const i of items) {
    if (!i.citySlug) continue;
    if (typeof i.latitude !== "number" || typeof i.longitude !== "number") continue;
    const cur = acc.get(i.citySlug) ?? { lat: 0, lng: 0, count: 0 };
    cur.lat += i.latitude;
    cur.lng += i.longitude;
    cur.count += 1;
    acc.set(i.citySlug, cur);
  }
  const out = new Map<string, CityCentroid>();
  for (const [slug, v] of acc) {
    out.set(slug, { lat: v.lat / v.count, lng: v.lng / v.count, count: v.count });
  }
  return out;
}

/**
 * A traveller in the suburbs is still in their city; a traveller in Madrid is
 * not in Tangier.
 */
export const NEARBY_CITY_MAX_KM = 100;
/** Below two mapped venues there is nothing to frame, so the city is no answer. */
const NEARBY_CITY_MIN_VENUES = 2;

function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

/** The city the given position is in, or null when it is in none of ours. */
export function nearestCitySlug(
  lat: number,
  lng: number,
  centroids: ReadonlyMap<string, CityCentroid>,
  maxKm: number = NEARBY_CITY_MAX_KM,
): string | null {
  let best: { slug: string; km: number } | null = null;
  for (const [slug, c] of centroids) {
    if (c.count < NEARBY_CITY_MIN_VENUES) continue;
    const km = haversineKm(lat, lng, c.lat, c.lng);
    if (!best || km < best.km) best = { slug, km };
  }
  return best && best.km <= maxKm ? best.slug : null;
}

// ─────────────────────────────────────────────────────────────────
// Cluster bubbles
// ─────────────────────────────────────────────────────────────────

/** Our controls stop the camera here (see clampedZoom), so this is as close as
 *  a traveller can ever get. */
const MAX_ZOOM = 20;
/** Roughly how wide a pin draws. */
const PIN_PX = 48;
/** Web Mercator puts the whole world in 256 px at zoom 0. */
const WORLD_PX_AT_ZOOM_0 = 256;

/**
 * Below this much spread, the members of a bubble are still stacked on top of
 * one another even at the closest zoom the map allows.
 */
export const COINCIDENT_SPAN_DEG =
  (PIN_PX * 360) / (WORLD_PX_AT_ZOOM_0 * 2 ** MAX_ZOOM);

/**
 * Whether a closer look would actually break this bubble apart.
 *
 * A mall, a corniche, a hotel with three restaurants inside it: those venues
 * share one coordinate, so they stay a single bubble at every zoom. Answering
 * every tap with more zoom leaves that bubble as a dead end, and a "14" the
 * traveller can never open is fourteen venues that, to them, do not exist.
 */
export function clusterCanSplit(
  points: readonly { readonly lat: number; readonly lng: number }[],
): boolean {
  if (points.length < 2) return false;
  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const span = Math.max(
    Math.max(...lats) - Math.min(...lats),
    Math.max(...lngs) - Math.min(...lngs),
  );
  return span > COINCIDENT_SPAN_DEG;
}

/** What a tap (or Enter/Space) on a cluster bubble should do. */
export type ClusterActivate = (cluster: Cluster, map: google.maps.Map) => void;

/**
 * The gold-on-navy count bubble. Built as a factory so the keyboard path and
 * the mouse path run the same handler — a bubble that opens on tap but only
 * zooms on Enter is two different features wearing one badge.
 */
export function createClusterRenderer(onActivate: ClusterActivate): Renderer {
  return {
    render: (cluster, _stats, map) => {
      const win = window as unknown as { google: typeof google };
      const el = document.createElement("div");
      el.className = "mj-marker mj-map-cluster";
      el.textContent = String(cluster.count);
      // Keyboard + screen-reader operable: a focusable button whose name is the
      // count; Enter/Space drills in, mirroring the clusterer's mouse click.
      el.tabIndex = 0;
      el.setAttribute("role", "button");
      el.addEventListener("keydown", (e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        onActivate(cluster, map);
      });
      return new win.google.maps.marker.AdvancedMarkerElement({
        position: cluster.position,
        map,
        content: el,
        zIndex: 50,
      });
    },
  };
}
